import type { ShopCard as ShopCardModel } from '@croissants/contracts';
import { guestChoosesShop } from '@croissants/contracts/projections';
import { Button, EmptyState, Modal, usePreloadWhenIdle } from '@croissants/ui';
import { type JSX, useState } from 'react';
import { Redirect, useLocation } from 'wouter';
import { AsyncPanel } from '../components/feedback';
import { useShops } from '../hooks/queries';
import { serviceDayLabel } from '../lib/format';
import { deliveryLine, distanceLabel, weeklyHoursPhrases } from '../lib/shop-facts';
import { shopChangeLosesBasket } from '../state/basket';
import { useBasket } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';
import { AFTER_THE_SHELF } from './lazy';

/**
 * Mode B - the brands that can serve this address, and the FIRST step of the
 * mode B journey (PRD GST-030, GST-031).
 *
 * The brand is chosen before the morning here, and that is deliberate: a brand
 * has many Paris counters, so what the guest is choosing is a house, not a shop
 * front. The picker spans every eligible counter of that house, and
 * which one actually serves is allocation's answer once the day and the window
 * are known - never something this screen decides or names.
 *
 * The list is the API's answer, unfiltered: it already contains only houses that
 * can genuinely serve this address, each carrying the first morning it can be
 * ordered for. A house shut tomorrow is not a choice that fails - it is a choice
 * with a later date on it, and it is still a choice. ONE entry per house, which is what `shop.id` keys this grid on - until
 * finding F-20 the endpoint answered one entry per COUNTER while stamping each
 * with its house's id, so a 1er apartment rendered twelve tiles under two names
 * and two React keys. Nothing here filtered or collapsed them and nothing here
 * should: a tile is a choice, and the server decides what the choices are.
 *
 * The card is a marketplace tile, not a form row: the shop's own hero visual
 * carries its logo at the size a shopfront sign is read at, and the WHOLE tile
 * is the control. There is no call-to-action button, because a button inside a
 * clickable tile is two targets for one action - and a native `<button>` is what
 * keeps Enter, Space, the focus ring and the disabled state working without a
 * line of keyboard code. The minimum basket moved off the tile with the button:
 * it is a checkout constraint, and it is stated at the basket and at checkout
 * (`checkout.minimumBasket`), where it can actually be acted on. What the tile
 * states instead is the pair a guest choosing between two houses is actually
 * asking about: how far it is, the week it keeps, and the first morning they
 * could order for.
 *
 * Changing shop clears the basket, and the guest is told that before the items are
 * lost (GST-032) - the confirmation is real, not a formality: the reducer refuses
 * a silent change, so this dialog is the only path to one.
 */
export function Shops(): JSX.Element {
  usePreloadWhenIdle(AFTER_THE_SHELF);
  const { t, pt } = useLocale();
  const { basket, dispatch } = useBasket();
  const { update } = useCheckoutDraft();
  const { session } = useSession();
  const [, navigate] = useLocation();
  const [pendingShopId, setPendingShopId] = useState<string | null>(null);

  /**
   * THIS SCREEN BELONGS TO THE MODE WITH A CHOOSER, and to no other.
   *
   * `GET /g/shops` refuses a mode B2 session outright - a list of named houses
   * is precisely what that mode does not show - so a bookmark, a stale tab or a
   * hand-typed `/shops` would open a screen whose only content is a refusal.
   * The journey decides instead, which for B2 is its own first screen
   * (`entryStep`, lib/journey.ts). Mode A has no chooser either and is sent the
   * same way.
   */
  const chooses =
    session !== null && session !== undefined && guestChoosesShop(session.storefrontMode);

  const query = useShops(chooses);
  if (!chooses) return <Redirect to="/" replace />;

  return (
    <AsyncPanel query={query}>
      {(data) => {
        const { shops } = data;

        /**
         * The house is chosen, so its counter is what comes next - the morning is
         * asked after the goods, from the basket button (App.tsx `Home`).
         *
         * Any morning already chosen against a DIFFERENT house is dropped on the way -
         * the WHOLE selection, not just the anchor. Those windows were that house's
         * offer; carrying one over would put a time on the basket that this house may
         * not be able to serve, and the guest would meet that at the Total rather than
         * at the picker. `selectedDays` has to go with `serviceDay` and `windowStart`
         * or the draft is left in a state no picker produces: an empty anchor beside a
         * stay of three mornings, which reads as nothing chosen on every screen that
         * asks the anchor and as three mornings on the strip that asks the set.
         */
        const adopt = (shopId: string): void => {
          if (shopId !== basket.shopId) {
            update({ serviceDay: null, windowStart: null, selectedDays: [] });
          }
          navigate('/menu');
        };

        const choose = (shopId: string): void => {
          if (shopChangeLosesBasket(basket, shopId)) {
            setPendingShopId(shopId);
            return;
          }
          dispatch({ type: 'SELECT_SHOP', shopId });
          adopt(shopId);
        };

        const confirmChange = (): void => {
          if (pendingShopId === null) return;
          dispatch({ type: 'FORCE_SELECT_SHOP', shopId: pendingShopId });
          setPendingShopId(null);
          adopt(pendingShopId);
        };

        return (
          <div className="gw-screen">
            <header className="gw-screen__header">
              <h1>{pt('storefront.chooseShop')}</h1>
              <p className="gw-muted">{t('shops.subtitle')}</p>
            </header>

            {shops.length === 0 ? (
              <EmptyState title={t('shops.empty')} />
            ) : (
              <div className="gw-shopgrid">
                {shops.map((shop) => (
                  <ShopEntry key={shop.id} shop={shop} onSelect={() => choose(shop.id)} />
                ))}
              </div>
            )}

            <Modal
              open={pendingShopId !== null}
              onClose={() => setPendingShopId(null)}
              title={t('shops.changeConfirmTitle')}
              closeLabel={pt('action.close')}
              actions={
                <>
                  <Button variant="ghost" onClick={() => setPendingShopId(null)}>
                    {pt('action.back')}
                  </Button>
                  <Button variant="danger" onClick={confirmChange}>
                    {t('shops.changeConfirmAction')}
                  </Button>
                </>
              }
            >
              <p>{pt('storefront.changeShopWarning')}</p>
            </Modal>
          </div>
        );
      }}
    </AsyncPanel>
  );
}

/**
 * One shop, as one control.
 *
 * The hero is the shop's own photograph and the logo sits on top of it at the
 * size a sign is read at - the identity is the point of a mode B chooser
 * (CAT-012, GST-030), and a 24px corner chip is a favicon, not a brand. A shop
 * that has sent no logo falls back to its name set as a wordmark in the same
 * place, so the tile never renders as an empty plate.
 *
 * `alt=""` on both images is deliberate: the shop's name is already the tile's
 * accessible name, in text, immediately below - announcing it twice is noise,
 * and neither image carries information the text does not.
 *
 * EVERY TILE IS CLICKABLE. It used to carry `disabled` for a house that could
 * not serve the coming morning, which made sense only while mode B asked for
 * the morning first: now the chooser comes before any date exists, so a dead
 * tile was refusing a guest over a choice they had not made yet. What a house
 * can and cannot serve is the picker's answer, after the basket - and the two
 * facts a guest is actually choosing between are on the tile instead: the
 * distance, the week, and the first morning they could order for.
 */
function ShopEntry({ shop, onSelect }: { shop: ShopCardModel; onSelect: () => void }): JSX.Element {
  const { locale, t } = useLocale();
  const { session } = useSession();
  const timeZone = session?.timeZone ?? 'Europe/Paris';
  const name = shop.displayName;
  /*
   * The HOUSE's own week: on each weekday, earliest any of its counters opens
   * to latest any of them closes, in the counters' zone (BAK-027).
   *
   * `weeklyPickupHours` folded that on the server with the same envelope rule
   * the picker's single morning uses (`pickupHoursSpan`, finding F-33), and
   * each span is printed by `pickupHoursLabel`, the shared projection the
   * bakery portal states a counter's day from - so two surfaces cannot render
   * one span two ways. The week replaced the single morning's span here
   * (founder, 2026-09-18): the morning's hours are one of the seven, so
   * printing both said the same thing twice.
   *
   * `?? []` and `?? null` are the deploy window, not a compatibility promise:
   * guest-web and the worker deploy side by side, and for the minute this
   * bundle is ahead of the worker the two fields are simply absent.
   */
  const week = weeklyHoursPhrases(shop.weeklyHours ?? [], locale, {
    everyDay: t('shops.everyDay'),
    closed: (days) => t('shops.closedOn', { days }),
  });
  const distance = shop.distanceMeters ?? null;
  /*
   * When this house delivers on the first morning it can be ordered for - and,
   * for a house that can be ordered from NOW, until when (founder, 2026-09-18).
   * Null falls back to the bare date the tile used to end on.
   */
  const delivery = deliveryLine(shop, locale, {
    today: (times) => t('shops.deliveryToday', { times }),
    tomorrow: (times) => t('shops.deliveryTomorrow', { times }),
    later: (day, times) => t('shops.deliveryLater', { day, times }),
    until: (time) => t('shops.deliveryUntil', { time }),
    closedUntil: (date) => t('shops.closedUntil', { date }),
  });

  return (
    <button
      type="button"
      className="gw-shoptile"
      onClick={onSelect}
      aria-label={`${name} - ${t('shops.select')}`}
    >
      <span className="gw-shoptile__hero">
        {shop.heroImageUrl === null ? null : (
          <img className="gw-shoptile__photo" src={shop.heroImageUrl} alt="" loading="lazy" />
        )}
        {shop.logoUrl === null ? (
          <span className="gw-shoptile__wordmark">{name}</span>
        ) : (
          <img className="gw-shoptile__logo" src={shop.logoUrl} alt="" loading="lazy" />
        )}
      </span>

      <span className="gw-shoptile__body">
        {/*
          The distance belongs to the name, in parentheses beside it: it is a
          fact about the house, read in the same glance. Straight-line metres to
          its nearest counter - which counter bakes is still allocation's
          answer, so this says how far the house is and names no shop.
        */}
        <span className="gw-shoptile__name">
          {name}
          {distance === null ? null : (
            <span className="gw-shoptile__distance"> ({distanceLabel(distance, locale)})</span>
          )}
        </span>

        {/*
          The week, directly under the name, unlabelled: day names followed by
          times read as opening hours in either language. Each phrase stays
          whole and the line breaks BETWEEN phrases, so a narrow tile never
          splits "Tue-Sat" from its hours.
        */}
        {week.length === 0 ? null : (
          <span className="gw-shoptile__week">
            {week.map((phrase) => (
              <span key={phrase} className="gw-shoptile__weekpart">
                {phrase}
              </span>
            ))}
          </span>
        )}

        {shop.shortDescription === '' ? null : (
          <span className="gw-shoptile__description">{shop.shortDescription}</span>
        )}

        {/* When it delivers, on the first morning it can be ordered for. The
            bare date is the fallback for a card with no run to state. */}
        {shop.nextAvailableServiceDay === null ? null : (
          <span className="gw-shoptile__when">
            {delivery ?? serviceDayLabel(shop.nextAvailableServiceDay, locale, timeZone)}
          </span>
        )}
      </span>
    </button>
  );
}
